import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from '../common/Loader.js';
import DateFilterDropdown from './DateFilterDropdown.js';
import vehicleContractService from '../../services/vehicleContractService';
import {
  Truck,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Gauge,
  ArrowLeft,
  IndianRupee,
} from 'lucide-react';
import Button from '../common/Button.js';
import {
  AnalyticsStatCard,
  AnalyticsTable,
  analyticsStatsGrid8Class,
} from './common/index.js';
import { formatCurrencyWhole as formatCurrency } from '../../utils/formatCurrency.js';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'expiring_soon', label: 'Expiring Soon' },
];

const VehicleContractAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [totals, setTotals] = useState({
    totalContracts: 0,
    totalCost: 0,
    totalPaid: 0,
    totalPending: 0,
    activeContracts: 0,
    expiredContracts: 0,
    totalKmsLimit: 0,
    totalKmsUsed: 0,
  });

  const [filter, setFilter] = useState('all');
  const [customDates, setCustomDates] = useState({ from_date: '', to_date: '' });
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [contracts, filter, customDates, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await vehicleContractService.getAll(1, 1000, { all: 'true' });
      const list = response.data.data || [];
      setContracts(list);
      setFilteredContracts(list);
      calculateTotals(list);
    } catch (error) {
      console.error('Error fetching vehicle contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (contractsData) => {
    const today = new Date();
    const next = contractsData.reduce(
      (acc, contract) => {
        acc.totalContracts += 1;
        acc.totalCost += contract.total_cost || 0;
        acc.totalPaid += contract.total_paid || 0;
        acc.totalPending += (contract.total_cost - contract.total_paid) || 0;
        acc.totalKmsLimit += contract.kms_limit || 0;
        acc.totalKmsUsed += contract.kms_used || 0;

        const toDate = new Date(contract.to_date);
        if (toDate > today) acc.activeContracts += 1;
        else acc.expiredContracts += 1;
        return acc;
      },
      {
        totalContracts: 0,
        totalCost: 0,
        totalPaid: 0,
        totalPending: 0,
        activeContracts: 0,
        expiredContracts: 0,
        totalKmsLimit: 0,
        totalKmsUsed: 0,
      }
    );
    setTotals(next);
  };

  const calculateDateRange = (filterKey, customFrom, customTo) => {
    const today = new Date();
    let startDate;
    let endDate;

    switch (filterKey) {
      case 'current_month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'last_3_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        endDate = today;
        break;
      case 'last_6_months':
        startDate = new Date(today.getFullYear(), today.getMonth() - 6, 1);
        endDate = today;
        break;
      case 'last_year':
        startDate = new Date(today.getFullYear() - 1, today.getMonth(), 1);
        endDate = today;
        break;
      case 'custom':
        if (customFrom && customTo) {
          startDate = new Date(customFrom);
          endDate = new Date(customTo);
        }
        break;
      default:
        return null;
    }

    if (startDate && endDate) {
      return {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
      };
    }
    return null;
  };

  const getContractStatus = (contract) => {
    const today = new Date();
    const toDate = new Date(contract.to_date);
    const daysLeft = Math.ceil((toDate - today) / (1000 * 60 * 60 * 24));
    if (toDate < today) return 'expired';
    if (daysLeft <= 5) return 'expiring_soon';
    return 'active';
  };

  const applyFilters = () => {
    let filtered = [...contracts];

    if (filter !== 'all') {
      const range = calculateDateRange(filter, customDates.from_date, customDates.to_date);
      if (range) {
        filtered = filtered.filter((contract) => {
          const contractDate = new Date(contract.created_at).toISOString().split('T')[0];
          return contractDate >= range.start_date && contractDate <= range.end_date;
        });
      } else if (filter === 'custom') {
        filtered = [];
      }
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((contract) => getContractStatus(contract) === statusFilter);
    }

    setFilteredContracts(filtered);
    calculateTotals(filtered);
  };

  if (loading) {
    return (
      <DashboardLayout fullWidth>
        <Loader fullScreen={false} size="default" message="Loading Vehicle Contract Analytics..." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout fullWidth>
      <div className="w-full">
        {/* Header — matches other analytics pages */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6 gap-3">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-semibold text-secondary">Vehicle Contract Analytics</h1>
            <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
              Monitor vehicle contract performance and usage metrics
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <DateFilterDropdown
              filter={filter}
              setFilter={setFilter}
              customDates={customDates}
              setCustomDates={setCustomDates}
              onApply={applyFilters}
            />
            <SelectField
              variant="form"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-sky-200 min-w-[140px]"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SelectField>
            <Button variant="primary" icon={ArrowLeft} onClick={() => navigate('/analytics')}>
              Back
            </Button>
          </div>
        </div>

        {/* Summary cards — soft pastels + ₹; 2 / 4 / 8 across breakpoints */}
        <div className={`${analyticsStatsGrid8Class} mb-4 md:mb-6`}>
          <AnalyticsStatCard
            title="Total Contracts"
            value={totals.totalContracts}
            subtitle="All contracts"
            icon={Truck}
            color="bg-blue-100 text-blue-600"
          />
          <AnalyticsStatCard
            title="Total Cost"
            value={formatCurrency(totals.totalCost)}
            subtitle="Contract cost"
            icon={IndianRupee}
            color="bg-emerald-100 text-emerald-600"
          />
          <AnalyticsStatCard
            title="Total Paid"
            value={formatCurrency(totals.totalPaid)}
            subtitle="Amount collected"
            icon={CheckCircle}
            color="bg-teal-100 text-teal-600"
          />
          <AnalyticsStatCard
            title="Pending"
            value={formatCurrency(totals.totalPending)}
            subtitle="Balance due"
            icon={Clock}
            color="bg-orange-100 text-orange-600"
          />
          <AnalyticsStatCard
            title="Active"
            value={totals.activeContracts}
            subtitle="In progress"
            icon={TrendingUp}
            color="bg-indigo-100 text-indigo-600"
          />
          <AnalyticsStatCard
            title="Expired"
            value={totals.expiredContracts}
            subtitle="Ended contracts"
            icon={AlertCircle}
            color="bg-rose-100 text-rose-600"
          />
          <AnalyticsStatCard
            title="KMs Limit"
            value={totals.totalKmsLimit.toLocaleString('en-IN')}
            subtitle="Total limit"
            icon={Gauge}
            color="bg-purple-100 text-purple-600"
          />
          <AnalyticsStatCard
            title="KMs Used"
            value={totals.totalKmsUsed.toLocaleString('en-IN')}
            subtitle="Total used"
            icon={Gauge}
            color="bg-violet-100 text-violet-600"
          />
        </div>

        <AnalyticsTable
          title="Vehicle Contracts Summary"
          columns={[
            { header: 'Contract ID', align: 'left' },
            { header: 'Vehicle', align: 'left' },
            { header: 'Start Date', align: 'left' },
            { header: 'End Date', align: 'left' },
            { header: 'Total Cost', align: 'right' },
            { header: 'Paid', align: 'right', cellClassName: 'text-emerald-600' },
            { header: 'KMs Used', align: 'right' },
            { header: 'KMs Limit', align: 'right' },
            { header: 'Status', align: 'left' },
            { header: 'Action', align: 'center' },
          ]}
          rows={filteredContracts.map((contract) => {
            const status = getContractStatus(contract);
            const statusColor =
              status === 'active'
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                : status === 'expiring_soon'
                  ? 'bg-amber-50 text-amber-700 border border-amber-100'
                  : 'bg-rose-50 text-rose-700 border border-rose-100';
            const statusLabel =
              status === 'active' ? 'Active' : status === 'expiring_soon' ? 'Expiring Soon' : 'Expired';
            return {
              key: contract.id,
              cells: [
                contract.id,
                contract.vehicle_number || 'N/A',
                new Date(contract.from_date).toLocaleDateString('en-IN'),
                new Date(contract.to_date).toLocaleDateString('en-IN'),
                formatCurrency(contract.total_cost),
                formatCurrency(contract.total_paid),
                (contract.kms_used || 0).toLocaleString('en-IN'),
                (contract.kms_limit || 0).toLocaleString('en-IN'),
                <span
                  key="status"
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor}`}
                >
                  {statusLabel}
                </span>,
                <button
                  key="action"
                  type="button"
                  onClick={() => navigate(`/vehicle-contracts/view/${contract.id}`)}
                  className="text-sky-700 hover:text-sky-900 font-medium text-sm"
                >
                  View
                </button>,
              ],
            };
          })}
          emptyMessage="No contracts found for the selected filters"
          tableMinWidth="960px"
        />
      </div>
    </DashboardLayout>
  );
};

export default VehicleContractAnalytics;
